import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { user, setUser, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const raw = params.get("next") || "/dashboard";
  const next = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(next, { replace: true }); }, [user, next, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setError(errMsg(err, "Could not sign in"));
    }
    setBusy(false);
  };

  return (
    <main className="max-w-md mx-auto px-5 py-20 relative z-10" data-testid="login-page">
      <h1 className="text-4xl font-extrabold tracking-tight">Sign in</h1>
      <p className="mt-2 text-sm text-slate-400">Build free. Unlock PDFs whenever you're ready.</p>
      <Button onClick={() => googleLogin(next)} data-testid="login-google-btn" className="mt-10 w-full h-12 rounded-full bg-white text-black font-semibold hover:bg-emerald-400 hover:text-black">
        Continue with Google
      </Button>
      <div className="my-8 flex items-center gap-4 text-xs font-mono uppercase tracking-widest text-slate-600">
        <span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" />
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs text-slate-400">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="h-11 bg-[#0c0e14] border-white/10" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-xs text-slate-400">Password</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-11 bg-[#0c0e14] border-white/10" />
        </div>
        {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full h-11 rounded-full bg-emerald-500 text-black font-semibold hover:bg-emerald-400 hover:text-black">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sign in with email
        </Button>
      </form>
    </main>
  );
}
